import type { Theme } from '@mui/material';
import { Box, Stack, Typography, useMediaQuery } from '@mui/material';
import type { FC } from 'react';
import { Button } from '~/components/atoms/Button';

import { PAGE_CONTENT_MAX_WIDTH } from '~/constants';

import type { CTAProps } from '~/types/sanity/components';
import { checkAuthVisibility } from '~/utils/auth';
import { getCompoundTestIdGenerator } from '~/utils/getCompoundTestIdGenerator';
import { buildSanityLinkUrl } from '~/utils/link';

export const CTA: FC<Omit<CTAProps, '_key' | '_type'>> = ({ title, links, isAuthenticated }) => {
  const smUp = useMediaQuery<Theme>((theme) => theme.breakpoints.up('sm'));
  const getCompoundTestId = getCompoundTestIdGenerator('cta');

  const visibleLinks = links ? links.filter((link) => checkAuthVisibility(link.authVisibility, isAuthenticated)) : [];

  return (
    <Box
      sx={{
        backgroundColor: ({ palette }) => palette.secondary.light,
        color: ({ palette }) => palette.secondary.contrastText,
        paddingX: { xs: 2, sm: 5 },
        paddingY: { xs: 5, sm: 8 },
      }}
      data-testid={getCompoundTestId()}
    >
      <Stack
        sx={{
          alignItems: { xs: 'flex-start', sm: 'center' },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 2, sm: 3 },
          justifyContent: 'space-between',
          margin: '0 auto',
          maxWidth: PAGE_CONTENT_MAX_WIDTH,
        }}
      >
        <Typography variant={smUp ? 'h3' : 'h4'} component="h3" data-testid={getCompoundTestId('title')}>
          {title}
        </Typography>
        {!!visibleLinks.length && (
          <Stack sx={{ flexDirection: 'row', gap: 4 }} data-testid={getCompoundTestId('buttons')}>
            {visibleLinks.map((link, index) => (
              <Button
                data-testid={getCompoundTestId('button', index.toString())}
                id={link.id}
                key={index}
                sx={{ whiteSpace: 'nowrap' }}
                to={buildSanityLinkUrl(link)}
                variant={index === visibleLinks.length - 1 ? 'contained' : 'outlined'}
              >
                {link.label}
              </Button>
            ))}
          </Stack>
        )}
      </Stack>
    </Box>
  );
};
